import { LoadingState } from "@repo/app-types/client/client";
import type { ApiKey } from "@repo/app-types/entities/apiKey";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Link } from "@repo/ui/Link/index";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconTrash } from "@tabler/icons-react";
import { format } from "date-fns";
import { type FC, useEffect, useState } from "react";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useAppDispatch } from "../../../hooks/hook.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsSectionPage } from "../shared/settingsSection.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../shared/settingsTableEmptyBodyContent.js";
import { SettingsTableErrorBodyContent } from "../shared/settingsTableErrorBodyContent.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { CreateApiKeyModal } from "./createApiKeyModal.js";
import { DeleteApiKeyModal } from "./deleteApiKeyModal.js";

export const DeveloperSettingsSection: FC = () => {
  const dispatch = useAppDispatch();

  const [listApiKeysLoadingState, setListApiKeysLoadingState] =
    useState<LoadingState>(LoadingState.LOADED);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [apiKeyToDelete, setApiKeyToDelete] = useState<ApiKey | null>(null);
  const [isCreateApiKeyModalOpen, setIsCreateApiKeyModalOpen] = useState(false);

  const fetchApiKeys = async () => {
    setListApiKeysLoadingState(LoadingState.LOADING);
    try {
      const axios = await getAxiosInstanceWithAuth();
      const { data } = await axios.get(`/api-keys`);

      setApiKeys(data);
      setListApiKeysLoadingState(LoadingState.LOADED);
    } catch (err) {
      setListApiKeysLoadingState(LoadingState.ERROR);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const renderTableRows = () => {
    if (listApiKeysLoadingState === LoadingState.LOADING)
      return <TableSkeleton />;

    if (listApiKeysLoadingState === LoadingState.ERROR)
      return (
        <SettingsTableErrorBodyContent colSpan={3}>
          An error occurred. Please refresh the page and try again.
        </SettingsTableErrorBodyContent>
      );

    if (apiKeys.length === 0)
      return (
        <SettingsTableEmptyBodyContent colSpan={4}>
          Create an API key to use the Alpharun API
        </SettingsTableEmptyBodyContent>
      );

    return (
      <>
        {apiKeys.map((apiKey) => (
          <tr className="group relative" key={apiKey.id}>
            <SettingsTableDataCell>{apiKey.name}</SettingsTableDataCell>
            <SettingsTableDataCell>{`*****${apiKey.key_suffix}`}</SettingsTableDataCell>
            <SettingsTableDataCell>
              {format(new Date(apiKey.created_at), "MMM dd yyyy")}

              <div className="absolute top-1/2 right-2 hidden -translate-y-1/2 cursor-pointer text-muted-foreground group-hover:block">
                <IconButton
                  variant={ButtonVariant.Secondary}
                  icon={<IconTrash className="h-4 w-4" />}
                  onClick={() => {
                    setApiKeyToDelete(apiKey);
                  }}
                />
              </div>
            </SettingsTableDataCell>
          </tr>
        ))}
      </>
    );
  };

  return (
    <SettingsSectionPage title={settingsSections.developers.label}>
      <SettingsTable>
        <SettingsTableHeaderSection>
          <tr>
            <SettingsTableHeaderCell className="w-[45%]">
              Name
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-[30%]">
              API Key
            </SettingsTableHeaderCell>
            <SettingsTableHeaderCell className="w-[25%]">
              Date created
            </SettingsTableHeaderCell>
          </tr>
        </SettingsTableHeaderSection>
        <SettingsTableBodySection>{renderTableRows()}</SettingsTableBodySection>
      </SettingsTable>
      <div className="mt-4 text-sm text-muted-foreground">
        API keys allow you to use the Alpharun API.{" "}
        <Link href="https://help.alpharun.com/en/articles/2476993">
          View API documentation here
        </Link>
        {"."}
      </div>
      <div className="mt-4">
        <Button
          variant={ButtonVariant.Primary}
          onClick={() => {
            setIsCreateApiKeyModalOpen(true);
          }}
        >
          Create API Key
        </Button>
      </div>
      <CreateApiKeyModal
        isOpen={isCreateApiKeyModalOpen}
        onClose={() => {
          setIsCreateApiKeyModalOpen(false);
          fetchApiKeys();
        }}
      />
      <DeleteApiKeyModal
        isOpen={Boolean(apiKeyToDelete)}
        onClose={() => {
          setApiKeyToDelete(null);
          fetchApiKeys();
        }}
        apiKeyToDelete={apiKeyToDelete}
      />
    </SettingsSectionPage>
  );
};
